import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { Dialog } from '@headlessui/react';
import clsx from 'clsx';

export const TermsModal = ({ isOpen, onClose, onAccept, termsContent }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isAcknowledged, setIsAcknowledged] = useState(false);
  const contentRef = useRef(null);

  const handleScroll = () => {
    if (contentRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 10) {
        setIsScrolled(true);
      }
    }
  };

  useEffect(() => {
    if (isOpen) {
      const savedAcknowledge = localStorage.getItem('isAcknowledge') === 'true';
      setIsAcknowledged(savedAcknowledge);
      if (savedAcknowledge) setIsScrolled(true);
    }
  }, [isOpen]);

  // Automatically set as scrolled if the content is short enough to fit without scrolling
  useEffect(() => {
    if (isOpen && contentRef.current) {
      const { scrollHeight, clientHeight } = contentRef.current;
      if (scrollHeight <= clientHeight) {
        setIsScrolled(true);
      }
    }
  }, [isOpen, termsContent]);

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        aria-hidden="true"
      />

      {/* Modal Container */}
      <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
        <Dialog.Panel className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col border border-gray-200 font-bai">
          {/* Header */}
          <div className="p-8 border-b border-gray-200 bg-gradient-to-r from-orange-400 via-orange-500 to-red-500 rounded-t-2xl">
            <Dialog.Title className="text-2xl font-bold text-black">
              Terms and Conditions
            </Dialog.Title>
            <p className="text-black/80 mt-2 font-medium">
              Please read and acknowledge the terms before proceeding
            </p>
          </div>

          {/* Content */}
          <div
            ref={contentRef}
            onScroll={handleScroll}
            className="p-8 overflow-y-auto flex-1 bg-white"
            style={{ maxHeight: '60vh' }}
          >
            <div className="prose prose-terms max-w-none prose-lg">
              <ReactMarkdown>{termsContent}</ReactMarkdown>
            </div>
          </div>

          {/* Footer */}
          <div className="p-8 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
            <div className="flex items-start mb-6">
              <input
                type="checkbox"
                id="acknowledge"
                checked={isAcknowledged}
                onChange={() => setIsAcknowledged(!isAcknowledged)}
                disabled={!isScrolled}
                className="h-5 w-5 text-red-500 bg-white border-gray-300 rounded focus:ring-red-500 focus:ring-offset-0 mt-0.5"
              />
              <label
                htmlFor="acknowledge"
                className={clsx('ml-3 block text-sm leading-relaxed', {
                  'text-gray-700': isScrolled,
                  'text-gray-500': !isScrolled,
                })}
              >
                I acknowledge that I have read and agree to the Terms and
                Conditions.
                {!isScrolled && (
                  <span className="block text-xs text-gray-400 mt-1">
                    Please scroll to the bottom to enable this option.
                  </span>
                )}
              </label>
            </div>

            <div className="flex justify-end space-x-4">
              <button
                onClick={onClose}
                className="px-8 py-3 text-sm font-bold text-gray-700 bg-white border border-gray-300 rounded-full hover:bg-gray-50 hover:border-gray-400 transition-colors"
              >
                Decline
              </button>
              <button
                onClick={onAccept}
                disabled={!isAcknowledged}
                className="px-8 py-3 text-sm font-bold text-white bg-black border border-black rounded-full hover:bg-gray-800 disabled:bg-gray-300 disabled:border-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                Accept & Continue
              </button>
            </div>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};
